import React, { useContext, useEffect } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import Navbar from "../../src/components/Navbar/USNavbar/Navbar";
import { ScrollDepthTracker } from "../utilities/ScrollDepthTracker";
import USModal from "../../src/components/Modals/USModal/USModal";
import ScrollToTop from "../../src/components/ScrollToTop/ScrollToTop";
import FindSurgeon from "../../src/components/Pages/USPages/Patients/FindSurgeon/FindSurgeon";
import ContactUs from "../../src/components/Pages/USPages/ContactUs/ContactUs";
import PrivacyPolicy from "../../src/components/Pages/USPages/PrivacyPolicy/PrivacyPolicy";
import Terms from "../../src/components/Pages/USPages/Terms/Terms";
import Home from "../../src/components/Pages/USPages/Home/Home";
import ClinicalStudies from "../../src/components/Pages/USPages/HealthcareProviders/ClinicalStudies/ClinicalStudies";
import Events from "../../src/components/Pages/USPages/HealthcareProviders/Events/Events";
import HealthcareProviders from "../../src/components/Pages/USPages/HealthcareProviders/HealthcareProviders";
import Patients from "../../src/components/Pages/USPages/Patients/Patients";
import PatientTestimonials from "../../src/components/Pages/USPages/Testimonials/Patients/Patient-testimonials";
import HCPTestimonials from "../../src/components/Pages/USPages/Testimonials/HCP/HCP-testimonials";
import RedirectPage from "../../src/components/Pages/USPages/Redirect/Redirect";
import NotFound from "../../src/components/Pages/USPages/NotFound/NotFound";
import Footer from "../../src/components/Footer/USFooter/Footer";
import { generateHreflangTags } from "../utilities/generateHreflangTags";
import SEO from "../components/SEO";
import {
  InitializeGA,
  InitializeGTM,
} from "../utilities/GoogleAnalyticsEvents";
import { Helmet } from "react-helmet-async";
import RegionContext from "../RegionContext";

const USLayout = () => {
  const region = useContext(RegionContext);
  const location = useLocation();

  useEffect(() => {
    InitializeGTM(region);
    InitializeGA(region);
  }, [region]);

  const seoData = {
    "/": {
      title: "Home - vNOTES",
      description:
        "Welcome to vNOTES - the next generation in minimally invasive gynecologic surgery.",
      keywords: "vnotes, minimally invasive surgery, gynecologic surgery",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `${window.location.origin}/`,
    },
    "/clinicalevidence": {
      title: "Clinical Evidence - vNOTES",
      description:
        "Explore clinical studies on vNOTES surgery to understand its efficacy and safety.",
      keywords:
        "vnotes clinical evidence, vnotes studies, gynecologic surgery research",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `${window.location.origin}/clinicalevidence`,
    },
    "/events": {
      title: "Events - vNOTES",
      description:
        "Join us at upcoming events to learn more about vNOTES surgical techniques and advancements.",
      keywords:
        "vnotes events, surgical conferences, gynecologic surgery events",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `${window.location.origin}/events`,
    },
    "/healthcareprofessionals": {
      title: "Healthcare Professionals - vNOTES",
      description:
        "Resources and training for healthcare professionals specializing in vNOTES.",
      keywords:
        "vnotes healthcare professionals, medical training, gynecologic surgery resources",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `${window.location.origin}/healthcareprofessionals`,
    },
    "/patients": {
      title: "For Patients - vNOTES",
      description:
        "Learn more about how vNOTES can provide a minimally invasive option for gynecologic surgery.",
      keywords: "vnotes for patients, minimally invasive gynecologic surgery",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `${window.location.origin}/patients`,
    },
    "/patientstestimonials": {
      title: "Patient Testimonials - vNOTES",
      description:
        "Hear from patients who have experienced the benefits of vNOTES surgery.",
      keywords: "vnotes testimonials, patient experiences, gynecologic surgery",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `${window.location.origin}/patientstestimonials`,
    },
    "/hcptestimonials": {
      title: "HCP Testimonials - vNOTES",
      description:
        "Testimonials from healthcare professionals on the efficacy of vNOTES.",
      keywords:
        "vnotes hcp testimonials, professional reviews, gynecologic surgery",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `${window.location.origin}/hcptestimonials`,
    },
    "/findasurgeon": {
      title: "Find a Surgeon - vNOTES",
      description:
        "Test description - Locate a certified surgeon trained in the vNOTES technique near you.",
      keywords: "find a vnotes surgeon, gynecologic surgery",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `${window.location.origin}/findasurgeon`,
    },
    "/contactus": {
      title: "Contact Us - vNOTES",
      description:
        "Get in touch with the vNOTES team for more information and support.",
      keywords: "vnotes contact, gynecologic surgery contact",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `${window.location.origin}/contactus`,
    },
  };

  // Retrieve metadata based on the current path
  const currentSEO = seoData[location.pathname] || {
    title: "vNOTES – Advanced Minimally Invasive Gynecologic Surgery",
    description:
      "vNOTES (vaginal natural orifice transluminal endoscopic surgery) modernizes vaginal surgery.",
    keywords: "vnotes, surgery, gynecology",
    image:
      "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
    url: `${window.location.origin}/${location.pathname}`,
  };

  // Generate hreflang tags for the current route and region
  const hreflangTags = generateHreflangTags(location.pathname, region) || [];

  return (
    <>
      <SEO
        title={currentSEO.title}
        description={currentSEO.description}
        keywords={currentSEO.keywords}
        image={currentSEO.image}
        url={currentSEO.url}
      />

      <Helmet>
        {hreflangTags.map((tag) => (
          <link
            key={tag.lang}
            rel="alternate"
            href={tag.url}
            hrefLang={tag.lang}
          />
        ))}
      </Helmet>

      <Navbar />
      <ScrollDepthTracker />
      <USModal />
      <ScrollToTop />

      <Switch>
        <Route path="/" exact component={Home} />
        <Route path="/clinicalevidence" exact component={ClinicalStudies} />
        <Route path="/clinicalevidence/:ref" component={ClinicalStudies} />
        <Route path="/events" exact component={Events} />
        <Route
          path="/healthcareprofessionals"
          exact
          component={HealthcareProviders}
        />
        <Route
          path="/healthcareprofessionals/:ref"
          component={HealthcareProviders}
        />
        <Route path="/patients" exact component={Patients} />
        <Route
          path="/patientstestimonials"
          exact
          component={PatientTestimonials}
        />
        <Route path="/hcptestimonials" exact component={HCPTestimonials} />
        <Route path="/findasurgeon" exact component={FindSurgeon} />
        <Route path="/contactus" exact component={ContactUs} />
        <Route path="/redirect" exact component={RedirectPage} />
        <Route path="/privacypolicy" exact component={PrivacyPolicy} />
        <Route path="/legalnotice" exact component={Terms} />
        <Route path="*" component={NotFound} />
      </Switch>
      <Footer />
    </>
  );
};

export default USLayout;
